<i18n>
{
  "en": {
    "noMenus": "No available menu recommendations",
    "errorMenus": "There was an error generating recommendations",
    "generatingMenus": "Recommendations are being generated",
    "saveMenuEngineeringSuccess": "Menu engineering recommendations confirmed successfully.",
    "saveMenuEngineeringError": "Error confirming menu engineering recommendations.",
    "demandUnavailable": "N/A",
    "confirmTitle": "Confirming changes for multiple menus",
    "confirmDescription": "Changes will be saved for menus",
    "confirm": "Confirm",
    "menus": "Optimize menus",
    "errorRecommendations": "There was an error generating recommendations for some of the menus, only the correct ones are displayed",
     "periodLoadingError": "Error updating period data"
  },
  "es": {
    "noMenus": "No hay recomendaciones de menús disponibles",
    "errorMenus": "Hubo algún error en la generación de recomendaciones",
    "generatingMenus": "Las recomendaciones se están generando",
    "saveMenuEngineeringSuccess": "Se han confirmado las recomendaciones de menu engineering correctamente.",
    "saveMenuEngineeringError": "Error confirmando las recomendaciones de menu engineering.",
    "demandUnavailable": "N/A",
    "confirmTitle": "Confirmando cambios para varios menús",
    "confirmDescription": "Se guardarán los cambios para los menús",
    "confirm": "Confirmar",
    "menus": "Optimizar menús",
    "errorRecommendations": "Hubo algún error en la generación de recomendaciones de alguno de los menús, se muestran los que no tienen errores",
    "periodLoadingError": "Error actualizando datos de periodo"
  }
}
</i18n>
<template>
  <v-container v-if="selectedPeriod">
    <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1"> {{ selectedPeriod.name }}</div>
    <div v-if="isEmptyAndNotLoading"
         class="d-flex justify-space-around empty-list grey--text">
      <span>{{ $t('noMenus') }}</span>
    </div>
    <div v-else>
      <v-container class="d-flex flex-wrap" elevation="1">
        <v-row>
          <v-col align-self="center" class="pa-0">
            <div class="text-center text-h5 mr-2">
              {{ $t('menus') }}
            </div>
          </v-col>
        </v-row>
      </v-container>
      <v-divider/>
      <dynameat-skeleton v-if="isLoading" skeleton-type="menuRecommendationSelector" :container="false"/>
      <v-list class="pb-14" v-if="isNotEmptyAndNotLoading">
        <v-card v-for="menuRecommendationPreview in menuRecommendationGeneratedList"
                :key="menuRecommendationPreview.guid">
          <dynameat-row :title="menuRecommendationPreview.title" center-title class="mb-2"
                        @click-content="onSelectMenu(menuRecommendationPreview)">
            <template v-slot:rowIconsRight>
              <div class="d-flex justify-end">
                <v-badge v-if="!menuRecommendationPreview.isRecommendationValidated && !isPreviousPeriod"
                   bordered color="error" icon="mdi-exclamation" class="mb-2"/>
              </div>
            </template>
          </dynameat-row>
        </v-card>
      </v-list>
    </div>
    <dynameat-confirmation-modal :visible="recommendationModalVisible" :title="$t('generatingMenus')"
                                 :is-loading="isLoading"/>
    <dynameat-confirmation-buttons @cancel="onCancel" :confirm-visible="false"/>
  </v-container>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import {
  Components, MapActions, MapGetters, Mixins
} from '@core/helpers/decorators';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import { checkPeriodMixin, periodMixins } from '@pointOfSale/mixins';
import DynameatConfirmationButtons from '@dynameat/components/DynameatConfirmationButtons';
import VueRouter from 'vue-router';
import { routeOutConfirmationMixin } from '@core/mixins';
import DynameatRow from '@dynameat/components/DynameatRow';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import EngineeringProfitabilityChart, { SIZE_TYPES } from '../components/EngineeringProfitabilityChart.vue';

const { isNavigationFailure, NavigationFailureType } = VueRouter;
Component.registerHooks(['beforeRouteLeave']);
@Component
@Components({
  DynameatConfirmationButtons,
  DynameatSkeleton,
  DynameatRow,
  DynameatConfirmationModal,
  EngineeringProfitabilityChart
})
@MapActions(['addNotification', 'menuRecommendationListRequest', 'setLoading'])
@MapGetters(['menuRecommendationList', 'isLoading', 'predictionPeriodList'])
@Mixins([periodMixins, routeOutConfirmationMixin, checkPeriodMixin('menu')])
export default class MenuRecommendationList extends Vue {
  get isEmptyAndNotLoading() {
    return this.menuRecommendationGeneratedList.length === 0 && !this.isLoading;
  }

  get isPreviousPeriod() {
    const period = this.predictionPeriodList.find((el) => el.guid === this.$route.params.periodGuid);
    return period?.isPreviousPeriod;
  }

  get isNotEmptyAndNotLoading() {
    return !(this.menuRecommendationGeneratedList.length === 0 || this.isLoading);
  }

  get menuRecommendationGeneratedList() {
    return this.menuRecommendationList.filter((rec) => rec.isGenerated());
  }

  get chartSizeType() {
    return SIZE_TYPES.SMALL;
  }

  async created() {
    try {
      await this.reloadPeriod();
      await this.menuRecommendationListRequest(this.$route.params.periodGuid);
      this.$emit('reload-profitability');
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('errorRecommendations')));
    }
  }

  beforeRouteLeave(to, from, next) {
    if (this.waiter) this.waiter.forceClear();
    if (this.isLoading) {
      this.setLoading(-1);
    }
    next();
  }

  onCancel() {
    this.$router.push({ name: 'optimizer', params: { periodGuid: this.$route.params.periodGuid } }).catch((e) => {
      if (!isNavigationFailure(e, NavigationFailureType.aborted)) {
        Promise.reject(e);
      }
    });
  }

  onSelectMenu(menuRecommendation) {
    this.$router.push({
      name: 'menu-recommendation',
      params: { periodGuid: this.$route.params.periodGuid, menuRecommendationGuid: menuRecommendation.guid }
    }).catch((e) => {
      if (!isNavigationFailure(e, NavigationFailureType.aborted)) {
        Promise.reject(e);
      }
    });
  }
}
</script>

<style lang="css">
/*TODO: add a condition to remove transform to DynameatRow so we don't have to do this*/
.clickable:hover {
  transform: none !important;
}
</style>
